import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import Icon from 'components/Icon'
import toast from 'lib/toast'
import { useDeleteStatusCheck } from 'queries/status-checks/use-delete-status-check'
import { messageFromResponseError } from 'utils/strings'

import type {
  ServiceStatusCheckFromStatusCheck,
  StatusCheck
} from 'types/api/status-checks/status-check'

/**
 * Type guard function to check if the status check is a StatusCheck
 *
 * Currently, the StatusCheckList component renders items of type StatusCheck to generate the list of status checks. For
 * some reason, all of the utility functions and contexts used in the forms are using ServiceStatusCheckFromStatusCheck. This component
 * is the only component that is used in both contexts. This type guard function determines if the status check that is
 * passed in to this component is a StatusCheck or a ServiceStatusCheckFromStatusCheck in order to determine the identifier of the status
 * check.
 *
 * TODO: Refactor the status checks form utilities and contexts to use StatusCheck instead of ServiceStatusCheckFromStatusCheck
 */
function isStatusCheck(
  statusCheck: StatusCheck | ServiceStatusCheckFromStatusCheck
): statusCheck is StatusCheck {
  return 'identifier' in statusCheck
}

export const DeleteStatusCheck: React.FC<{
  statusCheck: StatusCheck | ServiceStatusCheckFromStatusCheck
  onChanged: () => void
}> = ({ statusCheck, onChanged }) => {
  const [isModalOpen, setIsModalOpen] = useBoolean(false)

  const statusCheckId = isStatusCheck(statusCheck)
    ? statusCheck.identifier
    : statusCheck.id

  const { mutateAsync: deleteStatusCheck } = useDeleteStatusCheck(statusCheckId)

  const handleDelete = async () => {
    try {
      await deleteStatusCheck()
      onChanged()
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was a problem while removing the Health Check. Please try again.'
        )
      })
    } finally {
      setIsModalOpen(false)
    }
  }

  return (
    <Box>
      <Button
        color="error"
        data-test-delete-status-check={statusCheckId}
        data-track-action="delete-status-checks-open-dialog"
        data-track-category="status-checks" // TODO track anything different if it's PNI?
        data-track-on="click"
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="contained"
        onClick={() => setIsModalOpen(true)}
      >
        Delete
      </Button>
      <Dialog
        aria-labelledby="alert-dialog-title"
        data-test-status-check-delete-modal={statusCheckId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle color="error" id="alert-dialog-title">
          Delete Health Check?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Health Check?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-test-cancel-button
            data-track-action="delete-status-check-dialog-cancel"
            data-track-category="status-checks" // TODO track anything different if it's PNI?
            data-track-on="click"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            data-test-delete-button
            data-track-action="delete-status-check-dialog-delete"
            data-track-category="status-checks" // TODO track anything different if it's PNI?
            data-track-on="click"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
